import type { Edge, Node } from '@xyflow/react'
import { addEdge, createEdgeId, createNode } from '@/entities/flow'
import { canConnectNodes, getEdgesToReplaceForTarget } from '@/entities/node/lib'
import { getAllOps, resolveOpAcceptSourceTypes, resolveOpMaxSourceConnections, type OpMenuMeta } from '@/shared/lib/op-registry'
import { resolveActualTargetNodeType, resolveTargetToolbar, GENERATE_OPS } from './resolve-registered-op-menu-items'
import type { NodeMoreOpItem } from './types'

const DEFAULT_NODE_SIZE = {
  width: 260,
  height: 148,
}

function buildOpCustomData(item: NodeMoreOpItem, sourceNodeIds: string[] = []) {
  const createData = item.createCustomData?.({
    sourceNodeIds,
    targetNodeType: item.nodeType,
  }) ?? {}

  return {
    ...createData,
    op: item.op,
    title: item.label,
    toolbar: item.toolbar ?? '',
  }
}

function getSourceTypes(nodes: Node[], sourceNodeIds: string[]) {
  const sourceNodeIdSet = new Set(sourceNodeIds)
  return Array.from(
    new Set(
      nodes
        .filter((node) => sourceNodeIdSet.has(node.id) && typeof node.type === 'string')
        .map((node) => node.type as string),
    ),
  )
}

function acceptsAllSources(sourceTypes: string[], acceptSourceTypes?: string[]) {
  if (sourceTypes.length === 0) {
    return false
  }

  if (!acceptSourceTypes?.length) {
    return true
  }

  return sourceTypes.every((type) => acceptSourceTypes.includes(type))
}

export function resolveBulkSelectionOpMenuItems({
  nodes,
  sourceNodeIds,
}: {
  nodes: Node[]
  sourceNodeIds: string[]
}): NodeMoreOpItem[] {
  const sourceTypes = getSourceTypes(nodes, sourceNodeIds)
  const isListInput = sourceTypes.some((type) => type === 'gallery' || type === 'text-list' || type === 'audio-list' || type === 'video-list')
  const isMultipleInputs = sourceNodeIds.length > 1

  return getAllOps()
    .flatMap((definition) => {
      if (!definition.menu) {
        return []
      }

      const menus = Array.isArray(definition.menu) ? definition.menu : [definition.menu]

      return menus.flatMap((menu: OpMenuMeta) => {
        const baseTargetNodeType = menu.create?.nodeType
        if (!baseTargetNodeType) {
          return []
        }

        const effectiveTargetNodeType = resolveActualTargetNodeType(
          definition.op,
          baseTargetNodeType,
          isListInput,
          isMultipleInputs,
        )

        const acceptSourceTypes = resolveOpAcceptSourceTypes(definition, effectiveTargetNodeType)
        if (!acceptsAllSources(sourceTypes, acceptSourceTypes)) {
          return []
        }

        if (menu.sourceNodeTypes && !acceptsAllSources(sourceTypes, menu.sourceNodeTypes)) {
          return []
        }

        if (menu.sourceToolbars && menu.sourceToolbars.includes('previewCrop')) {
          return []
        }

        const Icon = menu.icon
        const label = menu.label ?? definition.label
        const order = menu.order ?? Number.MAX_SAFE_INTEGER

        if (definition.targetNodeTypes && !definition.targetNodeTypes.includes(effectiveTargetNodeType)) {
          return []
        }

        const maxSources = resolveOpMaxSourceConnections(definition, effectiveTargetNodeType)
        if (sourceNodeIds.length > maxSources) {
          return []
        }

        const items: { item: NodeMoreOpItem; order: number }[] = []

        const createItem = (targetNode: string, itemLabel: string) => ({
          item: {
            nodeType: targetNode,
            op: definition.op,
            icon: Icon ? <Icon className="h-4 w-4" /> : <span className="text-xs font-medium">{itemLabel[0]?.toUpperCase()}</span>,
            label: itemLabel,
            toolbar: resolveTargetToolbar(targetNode, menu.create.toolbar),
            disabled: false,
            targetNodeTypes: definition.targetNodeTypes,
            createCustomData: menu.create.createCustomData,
          },
          order,
        })

        if (GENERATE_OPS.has(definition.op) && isMultipleInputs && !isListInput) {
          const listTarget = baseTargetNodeType === 'image' ? 'gallery'
            : baseTargetNodeType === 'text' ? 'text-list'
              : baseTargetNodeType === 'audio' ? 'audio-list'
                : baseTargetNodeType === 'video' ? 'video-list'
                  : baseTargetNodeType

          items.push(createItem(baseTargetNodeType, label))
          if (listTarget !== baseTargetNodeType && (!definition.targetNodeTypes || definition.targetNodeTypes.includes(listTarget))) {
            items.push(createItem(listTarget, `${label}列表`))
          }
        } else {
          items.push(createItem(effectiveTargetNodeType, label))
        }

        return items
      })
    })
    .sort((left, right) => left.order - right.order || left.item.label.localeCompare(right.item.label, 'zh-CN'))
    .map((entry) => entry.item)
}

export function buildBulkConnectEdges({
  edges,
  nodes,
  sourceNodeIds,
  targetNodeId,
}: {
  edges: Edge[]
  nodes: Node[]
  sourceNodeIds: string[]
  targetNodeId: string
}) {
  let nextEdges = [...edges]
  let edgesToAdd: Edge[] = []
  const replacedNodeTypes = new Set<string>()

  sourceNodeIds.forEach((source) => {
    if (source === targetNodeId) {
      return
    }

    const canConnect = canConnectNodes({
      edges: nextEdges,
      nodes,
      source,
      target: targetNodeId,
    })

    if (!canConnect) {
      return
    }

    const sourceNode = nodes.find(n => n.id === source)
    const edgesToReplace = getEdgesToReplaceForTarget({
      edges: nextEdges,
      nodes,
      target: targetNodeId,
      source,
      incomingSourceNodeType: typeof sourceNode?.type === 'string' ? sourceNode.type : undefined,
    })

    if (edgesToReplace.length > 0) {
      edgesToReplace.forEach(e => {
        const replacedSourceNode = nodes.find(n => n.id === e.source)
        if (typeof replacedSourceNode?.type === 'string') {
          replacedNodeTypes.add(replacedSourceNode.type)
        }
      })
      const replaceIds = new Set(edgesToReplace.map(e => e.id))
      nextEdges = nextEdges.filter(e => !replaceIds.has(e.id))
      edgesToAdd = edgesToAdd.filter(e => !replaceIds.has(e.id))
    }

    const edge: Edge = {
      id: createEdgeId({ source, target: targetNodeId }),
      source,
      target: targetNodeId,
    }

    edgesToAdd.push(edge)
    nextEdges.push(edge)
  })

  return { edgesToAdd, replacedNodeTypes: Array.from(replacedNodeTypes) }
}

export function createLinkedOpNodeFromBulkSelection({
  edges,
  flowPosition,
  item,
  nodes,
  sourceNodeIds,
}: {
  edges: Edge[]
  flowPosition: { x: number; y: number }
  item: NodeMoreOpItem
  nodes: Node[]
  sourceNodeIds: string[]
}) {
  const position = {
    x: flowPosition.x,
    y: flowPosition.y - DEFAULT_NODE_SIZE.height / 2,
  }
  const { id: targetNodeId } = createNode({
    customData: buildOpCustomData(item, sourceNodeIds),
    position,
    type: item.nodeType,
  })
  const nextNodes = [
    ...nodes,
    {
      id: targetNodeId,
      position,
      type: item.nodeType,
    } as Node,
  ]
  const { edgesToAdd, replacedNodeTypes } = buildBulkConnectEdges({
    edges,
    nodes: nextNodes,
    sourceNodeIds,
    targetNodeId,
  })

  edgesToAdd.forEach((edge) => addEdge(edge))

  return {
    edgeIds: edgesToAdd.map((edge) => edge.id),
    nodeId: targetNodeId,
    replacedNodeTypes,
  }
}
